import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { useState } from 'react';
import { Card } from 'react-bootstrap';
import EmojiPicker from 'components/base/EmojiPicker';
import { useChatContext } from 'providers/ChatProvider';
import ReactTextareaAutosize from 'react-textarea-autosize';
import AttachmentPreview from 'components/common/AttachmentPreview';
import { convertFileToAttachment } from 'helpers/utils';
import ImageAttachmentPreview from 'components/common/ImageAttachmentPreview';
import { faFaceSmile } from '@fortawesome/free-regular-svg-icons';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { SENT_MESSAGE } from 'reducers/ChatReducer';

interface ChatContentFooterProps {
  onSend?: (questionText: string) => void;
  minimal?: boolean;
}

const ChatContentFooter = ({ onSend, minimal = false }: ChatContentFooterProps) => {
  const { currentConversation, chatDispatch } = useChatContext();

  const [messageText, setMessageText] = useState('');
  const [fileAttachment, setFileAttachment] = useState<File | null>(null);
  const [imageAttachments, setImageAttachments] = useState<File[]>([]);

  const sentMessage = () => {
    if (!messageText && !fileAttachment && imageAttachments.length === 0) {
      return;
    }

    // If onSend prop is provided, use it to send the message
    if (onSend) {
      onSend(messageText);
      // Reset fields
      setMessageText('');
      setImageAttachments([]);
      setFileAttachment(null);
    } else {
      // Otherwise, use the existing dispatch logic
      if (currentConversation) {
        chatDispatch({
          type: SENT_MESSAGE,
          payload: {
            conversationId: currentConversation.id,
            message: messageText,
            attachments: {
              images: imageAttachments.map(imageAttachment => URL.createObjectURL(imageAttachment)),
              file: fileAttachment ? convertFileToAttachment(fileAttachment) : undefined
            }
          }
        });
        setMessageText('');
        setImageAttachments([]);
        setFileAttachment(null);
      }
    }
  };

  if (minimal) {
    return (
      <Card.Footer
        style={{
          borderTop: '1px solid #eef2f7',
          background: 'linear-gradient(to bottom, #fafbfc 0%, #ffffff 100%)',
          padding: '14px 16px 16px'
        }}
      >
        <div className="d-flex align-items-end gap-3">
          <div
            style={{
              flex: 1,
              minWidth: 0,
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              padding: '10px 14px',
              boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.04)'
            }}
          >
            <ReactTextareaAutosize
              minRows={1}
              maxRows={4}
              value={messageText}
              placeholder="Type your message..."
              onChange={({ target: { value } }) => setMessageText(value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sentMessage();
                }
              }}
              className="chat-textarea form-control outline-none border-0 shadow-none resize-none p-0 fs-8 fw-normal bg-transparent"
            />
          </div>
          <Button
            variant="primary"
            endIcon={<FontAwesomeIcon icon={faPaperPlane} className="ms-1" />}
            className="px-4 py-2 fs-10 flex-shrink-0 rounded-3"
            style={{ boxShadow: '0 2px 8px rgba(37, 99, 235, 0.35)' }}
            type="submit"
            onClick={sentMessage}
          >
            Send
          </Button>
        </div>
      </Card.Footer>
    );
  }

  return (
    <Card.Footer style={{ borderTop: '1px solid #eef2f7', backgroundColor: '#fbfdff' }}>
      <ReactTextareaAutosize
        minRows={1}
        maxRows={5}
        value={messageText}
        placeholder="Ask Ethico about AI policy and compliance..."
        onChange={({ target: { value } }) => setMessageText(value)}
        className="chat-textarea form-control outline-none border-0 scrollbar resize-none mb-2 p-0 fs-8 fw-normal"
      />

      {fileAttachment && (
        <div className="mb-2">
          <AttachmentPreview
            attachment={convertFileToAttachment(fileAttachment)}
            size="xl"
            handleRemove={() => setFileAttachment(null)}
          />
        </div>
      )}

      {imageAttachments && (
        <div className="mb-2 d-flex gap-2">
          {imageAttachments.map((attachment, index) => (
            <ImageAttachmentPreview
              key={index}
              image={URL.createObjectURL(attachment)}
              handleClose={() => {
                setImageAttachments(imageAttachments.filter((_, i) => index !== i));
              }}
            />
          ))}
        </div>
      )}

      <div className="d-flex gap-2 align-items-center">
        {!minimal && (
          <EmojiPicker
            onSelect={selection => {
              setMessageText(messageText => messageText + selection.emoji);
            }}
          >
            <Button variant="link" className="p-0 text-body fs-9 btn-emoji">
              <FontAwesomeIcon icon={faFaceSmile} />
            </Button>
          </EmojiPicker>
        )}

        <Button
          variant="primary"
          endIcon={<FontAwesomeIcon icon={faPaperPlane} className="ms-2" />}
          className="ms-auto px-3"
          type="submit"
          onClick={sentMessage}
        >
          Send
        </Button>
      </div>
    </Card.Footer>
  );
};

export default ChatContentFooter;
